import { useEffect, useState } from "react";
import { ArrowRight, Download } from "lucide-react";
import { useToast } from "../../components/Toast";
import { BottomNav } from "./ClientNav";

type UpdateRow = {
  pkg: string;
  name: string;
  iconColor: string;
  letter: string;
  from: string;
  to: string;
  sizeMb: number;
  isDelta: boolean;
  progress?: number; // 0..1 если идёт загрузка
};

const initialUpdates: UpdateRow[] = [
  { pkg: "ru.corp.docs",   name: "Документооборот", iconColor: "#7c3aed", letter: "Д", from: "4.1.2", to: "4.2.0", sizeMb: 2.1,  isDelta: true },
  { pkg: "ru.corp.vcs",    name: "ВКС-связь",       iconColor: "#0891b2", letter: "В", from: "2.1.0", to: "2.1.3", sizeMb: 5.4,  isDelta: true, progress: 0.64 },
  { pkg: "ru.corp.map",    name: "Карта объектов",  iconColor: "#16a34a", letter: "К", from: "1.1.8", to: "1.2.0", sizeMb: 12.0, isDelta: true },
  { pkg: "ru.corp.passes", name: "Заявки",          iconColor: "#059669", letter: "З", from: "1.0.5", to: "1.0.7", sizeMb: 0.4,  isDelta: true },
  { pkg: "ru.corp.mail",   name: "Корп.почта",      iconColor: "#2563eb", letter: "К", from: "5.0.0", to: "5.0.2", sizeMb: 1.8,  isDelta: true },
];

export default function UpdatesPage() {
  const push = useToast();
  const [batch, setBatch] = useState(0);
  const totalMb = initialUpdates.reduce((s, x) => s + x.sizeMb, 0);

  return (
    <div className="flex h-full flex-col">
      <header className="px-4 pb-2 pt-2">
        <h1 className="text-[22px] font-semibold tracking-[-0.01em] text-[var(--color-fg)]">Обновления</h1>
        <div className="mt-0.5 text-[12px] text-[var(--color-fg-muted)]">
          {initialUpdates.length} приложений · {totalMb.toFixed(1)} MB · через <span className="font-mono">msk-01</span>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-4 pb-4">
        {/* Большая кнопка «Обновить всё» */}
        <button
          onClick={() => {
            setBatch((value) => value + 1);
            push({ kind: "info", text: "Обновление всех приложений запущено" });
          }}
          className="mt-2 flex w-full items-center gap-3 rounded-2xl bg-[var(--color-brand)] px-3 py-3 text-left text-white transition-all hover:bg-[var(--color-navy-700)]"
        >
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[var(--color-surface)]/15">
            <Download size={18} />
          </span>
          <div className="flex-1">
            <div className="text-[14px] font-semibold">Обновить всё</div>
            <div className="text-[11.5px] opacity-80">{totalMb.toFixed(1)} MB · ~ 18 сек</div>
          </div>
          <ArrowRight size={18} />
        </button>

        <ul className="mt-4 flex flex-col gap-3">
          {initialUpdates.map((u, i) => (
            <li key={u.pkg} style={{ animationDelay: `${i * 35}ms` }} className="stagger-up">
              <UpdateItem row={u} batch={batch} />
            </li>
          ))}
        </ul>
      </div>

      <BottomNav />
    </div>
  );
}

function UpdateItem({ row, batch }: { row: UpdateRow; batch: number }) {
  const push = useToast();
  const [progress, setProgress] = useState<number | undefined>(row.progress);
  const [done, setDone] = useState(false);

  const click = () => {
    if (progress !== undefined || done) return;
    setProgress(0.05);
    const id = setInterval(() => {
      setProgress((p) => {
        const next = (p ?? 0) + 0.18;
        if (next >= 1) {
          clearInterval(id);
          setDone(true);
          push({ kind: "success", text: `${row.name} обновлено до ${row.to}` });
          return 1;
        }
        return next;
      });
    }, 220);
  };

  useEffect(() => {
    if (batch > 0) click();
    // click reads the current row state; batch is the intentional trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batch]);

  return (
    <div className="flex w-full items-center gap-3">
      <span
        className="grid shrink-0 place-items-center rounded-2xl font-semibold text-white"
        style={{ background: row.iconColor, width: 44, height: 44, fontSize: 18 }}
      >
        {row.letter}
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[14px] font-medium text-[var(--color-fg)]">{row.name}</div>
        <div className="mt-0.5 truncate font-mono text-[11px] text-[var(--color-fg-muted)]">
          {row.from} → <span className="text-[var(--color-fg)]">{row.to}</span> · {row.sizeMb} MB · {row.isDelta ? "дельта" : "полный APK"}
        </div>
        {progress !== undefined && !done && (
          <div className="mt-1.5 flex items-center gap-2">
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[var(--color-surface-3)]">
              <div className="h-full rounded-full bg-[var(--color-brand)] transition-[width]" style={{ width: `${progress * 100}%` }} />
            </div>
            <span className="w-9 shrink-0 text-right font-mono text-[10.5px] text-[var(--color-fg-muted)]">{Math.round(progress * 100)}%</span>
          </div>
        )}
      </div>
      <button
        onClick={click}
        disabled={progress !== undefined && !done}
        className={
          "shrink-0 rounded-full px-3 py-1.5 text-[12px] font-medium transition-colors " +
          (done
            ? "bg-[var(--color-success-bg)] text-[var(--color-success)]"
            : progress !== undefined
            ? "bg-[var(--color-surface-3)] text-[var(--color-fg-muted)]"
            : "bg-[var(--color-brand)] text-white")
        }
      >
        {done ? "Готово" : progress !== undefined ? `${Math.round(progress * 100)}%` : "Обновить"}
      </button>
    </div>
  );
}
